import axios from 'axios'
import { get } from 'lodash'
import { getUiServicesEndpoint } from './utils'
import {
  ShipmentModificationVariables,
  ShipmentRowInterface,
} from '../common/types'
import dayjs from 'dayjs'
import { pickupDateChangeReasons } from 'constants/pickupDateChangeReason'

export const getVendorRelease = (
  vendorRelease?: string,
  isModified?: boolean,
  isTloOnlyRemoval?: boolean,
) => {
  if (isTloOnlyRemoval && vendorRelease === 'PENDING_REVIEW') {
    return 'PENDING_REVIEW'
  }
  if (vendorRelease === 'REREVIEW_REQUIRED' && isModified) {
    return 'REREVIEW_REQUIRED'
  }
  return 'VENDOR_ACKNOWLEDGED'
}

/**
 * Returns true when the only modification on a PENDING_REVIEW shipment made by
 * a Target internal admin is the removal of the TLO reference ID.  In that
 * case the shipment must NOT be automatically marked as reviewed.
 */
export const isTloOnlyRemoval = (
  shipment: ShipmentRowInterface,
  isTgtInternalAdmin: boolean,
): boolean => {
  if (!isTgtInternalAdmin) return false
  if (shipment.vendorRelease !== 'PENDING_REVIEW') return false

  // TLO must be going from a value to empty/null
  if (shipment.original_tloRefId == null || !!shipment.tloRefId) return false

  // Every other tracked field must be unchanged
  const trackedFields = [
    'appointmentNumber',
    'bol',
    'carton',
    'commodityCode',
    'pallet',
    'pickupDate',
    'pickupReasonCode',
    'totalVol',
    'totalWt',
    'trailerId',
    'sealNumber',
  ] as const

  return trackedFields.every(
    (field) =>
      (shipment as any)[field] === (shipment as any)[`original_${field}`],
  )
}

export const transformShipmentsToModificationRequests = (
  shipments: Array<ShipmentRowInterface>,
  isTgtInternalAdmin: boolean = false,
) => {
  return {
    modification_requests: shipments.map((shipment: ShipmentRowInterface) => {
      const {
        tloRefId,
        appointmentNumber,
        bookingId,
        bookingVersion,
        bol,
        carton,
        commodityCode,
        isModified,
        origLocCode,
        pallet,
        pickupDate,
        pickupReasonCode,
        totalVol,
        totalWt,
        vendorRelease,
        volUom,
        wtUom,
        vop,
        trailerId,
        sealNumber,
        reverseLogisticsReferenceNumber,
        returnAuthNumber,
      } = shipment

      const hasPickupOverride =
        !!pickupReasonCode &&
        pickupReasonCode !==
          pickupDateChangeReasons.TGT_DRIVEN_PICKUP_DATE_OVERRIDE.label

      const tloOnly = isTloOnlyRemoval(shipment, isTgtInternalAdmin)
      const vendorReleaseValue = getVendorRelease(
        vendorRelease,
        isModified,
        tloOnly,
      )

      return {
        booking_id: bookingId,
        booking_version: bookingVersion,
        modified_attributes: {
          origin_location: {
            code: origLocCode,
            vop,
          },
          truck_load_optimization_ref_id: tloRefId,
          commodity_code: commodityCode,
          measures: {
            carton_quantity: carton,
            pallet: pallet,
            weight: {
              total: totalWt,
              unit_of_measure: wtUom,
            },
            volume: {
              total: totalVol,
              unit_of_measure: volUom,
            },
          },
          trailer_id: trailerId,
          seal_number: sealNumber,
          reverse_logistics_reference_number: reverseLogisticsReferenceNumber,
          return_authorization_number: returnAuthNumber,
          ...(hasPickupOverride && {
            pickup_override: {
              date: dayjs(pickupDate).format('YYYY-MM-DD'),
              change_reason: pickupReasonCode,
            },
          }),
          ...(vendorReleaseValue !== undefined && {
            vendor_release: vendorReleaseValue,
          }),
          appointment_number: appointmentNumber,
          bill_of_lading: bol,
        },
      }
    }),
  }
}

export const shipmentsModificationEndpoint = async ({
  shipments,
  uid,
  isTgtInternalAdmin = false,
}: ShipmentModificationVariables) => {
  const modifiedShipments = shipments.filter((shipment: any) => {
    return !shipment?.toBeCancelled && !shipment?.loadId
  })
  const transformedShipments = transformShipmentsToModificationRequests(
    modifiedShipments,
    isTgtInternalAdmin,
  )

  if (uid <= 0) {
    return
  }
  if (modifiedShipments.length <= 0) {
    return
  }
  const { key, url } = await getUiServicesEndpoint()
  const endpoint = `${url}/v1/shipment_modifications?key=${key}`
  try {
    const response = await axios<ShipmentModificationVariables>({
      method: 'post',
      url: endpoint,
      data: transformedShipments,
    })

    const failures = get(response, 'data.failures', []).map((failure: any) => {
      return {
        booking_id: failure.booking_id,
        status: failure.status,
      }
    })
    const failedBookingIds = failures.map((failure: any) => failure.booking_id)
    const failedShipments = shipments
      .filter((shipment: any) => failedBookingIds.includes(shipment.bookingId))
      .map((shipment: any) => {
        const failure = failures.find(
          (f: any) => f.booking_id === shipment.bookingId,
        )
        return {
          ...shipment,
          failureStatus: failure?.status,
        }
      })
    return failedShipments
    // eslint-disable-next-line
  } catch (err: any) {
    return modifiedShipments
  }
}
